import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Badge, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';

// "tot" = nie geprüft oder seit > 30 Tagen nicht mehr abgefragt
const STALE_DAYS = 30;
function _usageBadge(l) {
  if (l.revoked_at) return null;
  if (!l.last_checked_at) return <Badge bg="" className="badge-soft-secondary">nie genutzt</Badge>;
  const ageDays = (Date.now() - new Date(l.last_checked_at).getTime()) / 86_400_000;
  if (ageDays > STALE_DAYS) return <Badge bg="" className="badge-soft-warning" title={`zuletzt vor ${Math.round(ageDays)} Tagen`}>inaktiv</Badge>;
  if (ageDays < 1)         return <Badge bg="" className="badge-soft-success">live</Badge>;
  return <Badge bg="" className="badge-soft-success">aktiv</Badge>;
}
function _fmtAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1)      return 'gerade eben';
  if (min < 60)     return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24)       return `vor ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30)       return `vor ${d} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE');
}

export default function LicensesPage() {
  const qc = useQueryClient();
  const [sort, setSort] = useState('created');  // created | last_checked | check_count
  const licenses = useQuery({
    queryKey: ['licenses-all'],
    queryFn: () => api.get('/licenses', { params: { limit: 500 } }).then(r => r.data.items)
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/licenses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['licenses-all'] }),
    onError: (e) => alert(e.response?.data?.message || e.message)
  });
  const customers = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => api.get('/customers', { params: { limit: 1000, include_archived: true } }).then(r => r.data.items)
  });

  const customerById = Object.fromEntries((customers.data || []).map(c => [c.id, c]));

  const sorted = useMemo(() => {
    const list = [...(licenses.data || [])];
    if (sort === 'last_checked') {
      list.sort((a, b) => (b.last_checked_at || '').localeCompare(a.last_checked_at || ''));
    } else if (sort === 'check_count') {
      list.sort((a, b) => (b.check_count || 0) - (a.check_count || 0));
    } else {
      list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
    return list;
  }, [licenses.data, sort]);

  // Mini-Statistik
  const stats = useMemo(() => {
    const arr = licenses.data || [];
    const active = arr.filter(l => !l.revoked_at && !(l.valid_until && new Date(l.valid_until) < new Date()));
    const live = active.filter(l => l.last_checked_at && (Date.now() - new Date(l.last_checked_at).getTime()) < 86_400_000);
    const stale = active.filter(l => l.last_checked_at && (Date.now() - new Date(l.last_checked_at).getTime()) > STALE_DAYS * 86_400_000);
    const never = active.filter(l => !l.last_checked_at);
    return { total: arr.length, active: active.length, live: live.length, stale: stale.length, never: never.length };
  }, [licenses.data]);

  return (
    <>
      <PageHeader
        title="Lizenzen"
        subtitle={
          <>
            {stats.total} Lizenz{stats.total === 1 ? '' : 'en'} · {stats.active} aktiv ·{' '}
            <span className="text-success">{stats.live} live (24h)</span> ·{' '}
            <span className="text-warning">{stats.stale} inaktiv (&gt;{STALE_DAYS}d)</span> ·{' '}
            <span className="text-muted">{stats.never} nie genutzt</span>
          </>
        }
        actions={
          <>
            <Link to="/licenses/configure" className="btn btn-primary">
              <Icon icon="tabler:adjustments" className="me-1" />Konfigurator
            </Link>
            <Link to="/licenses/new" className="btn btn-outline-secondary">
              <Icon icon="tabler:plus" className="me-1" />Klassisch anlegen
            </Link>
          </>
        }
      />

      <div className="d-flex gap-2 mb-3">
        <Form.Select size="sm" style={{ width: 220 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="created">Sortierung: Erstellungs-Datum</option>
          <option value="last_checked">Sortierung: Zuletzt genutzt</option>
          <option value="check_count">Sortierung: Anzahl Aufrufe</option>
        </Form.Select>
      </div>

      <Card className="card-flat">
        <Card.Body className="p-0">
          {licenses.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {licenses.data && licenses.data.length === 0 && (
            <div className="empty-state">
              <Icon icon="tabler:certificate-off" className="empty-icon" />
              <div>Noch keine Lizenzen ausgestellt.</div>
            </div>
          )}
          {licenses.data && licenses.data.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Kunde</th>
                  <th>Status</th>
                  <th>Nutzung</th>
                  <th>Zuletzt geprüft</th>
                  <th className="text-end">Aufrufe</th>
                  <th>Gültig bis</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(l => {
                  const c = customerById[l.customer_id];
                  return (
                    <tr key={l.id}>
                      <td>
                        <Link to={`/licenses/${l.id}`} className="text-decoration-none">
                          <span className="fw-semibold">{l.name}</span>
                        </Link>
                        {l.plan_label && <div className="text-muted small">{l.plan_label}</div>}
                      </td>
                      <td>{c ? c.name : <span className="text-muted">—</span>}</td>
                      <td>
                        {l.revoked_at
                          ? (l.license_kind === 'download' && l.revoke_reason === 'auto_revoke_download'
                              ? <Badge bg="" className="badge-soft-info">Heruntergeladen</Badge>
                              : <Badge bg="" className="badge-soft-danger">Widerrufen</Badge>)
                          : (l.valid_until && new Date(l.valid_until) < new Date())
                            ? <Badge bg="" className="badge-soft-warning">Abgelaufen</Badge>
                            : <Badge bg="" className="badge-soft-success">Aktiv</Badge>}
                      </td>
                      <td>{_usageBadge(l)}</td>
                      <td className="small text-muted">{_fmtAgo(l.last_checked_at)}</td>
                      <td className="text-end">{l.check_count > 0 ? l.check_count.toLocaleString('de-DE') : <span className="text-muted">0</span>}</td>
                      <td>{l.valid_until ? new Date(l.valid_until).toLocaleDateString('de-DE') : <span className="text-muted">—</span>}</td>
                      <td className="text-end pe-3">
                        <Button as={Link} to={`/licenses/${l.id}`} size="sm" variant="outline-secondary" className="me-1" title="Öffnen">
                          <Icon icon="tabler:eye" />
                        </Button>
                        {l.revoked_at && (
                          <Button size="sm" variant="outline-danger" title="Endgültig löschen"
                            onClick={() => confirm(`Lizenz "${l.name}" unwiderruflich löschen?`) && del.mutate(l.id)}>
                            <Icon icon="tabler:trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
